import { api } from '@/lib/api'
import { deviceHeaders, type VerificationState } from '@/lib/verificationConnexion'
import type { Provenance } from '@/lib/provenance'

export interface LoginInput {
  email: string
  password: string
}

export interface LoginResponse {
  access: string
  refresh: string
  /** Present when the sign-in is risky: the code screen must be passed first. */
  verification?: VerificationState
}

export interface UserProfile {
  id?: number
  email?: string
  /** Date d'inscription (ISO). */
  date_joined?: string
  first_name?: string
  last_name?: string
  role?: 'AGENT' | 'MANAGER' | string
  language?: string
  /** L'app iOS ou Android s'est déjà connectée. */
  mobile_app_installed?: boolean
  company?: number
  company_name?: string
  phone_number?: string
  job_title?: string
  forward_number?: string
  dnd_enabled?: boolean
  sms_push_notifications_enabled?: boolean
  sms_email_notifications_enabled?: boolean
  avatar_url?: string
  preferred_caller_id?: string
  /** Cellulaire vérifié par code texto à l'onboarding. */
  phone_verified_at?: string | null
  phone_country?: string
  /** Vrai tant qu'un compte récent n'a pas vérifié son cellulaire (bloque l'achat du numéro et l'agent IA). */
  phone_verification_required?: boolean
  /** Linked account: the partner that sets the email and mobile number ('' otherwise). */
  linked_via?: string
  /** Role and recording access in the company (services/acces.py). Null without a company. */
  access?: UserAccess | null
}

export type AccessRole = 'owner' | 'manager' | 'supervisor' | 'member'
export type RecordingScope = 'all' | 'own' | 'none'

export interface UserAccess {
  role: AccessRole
  /** Which call recordings (audio and transcript) this person hears. */
  recordings: RecordingScope
  /** Delete and download recordings. */
  manage_recordings: boolean
  billing: boolean
  /** Team analytics and live calls. */
  team_insights: boolean
}

export interface CompanyInfo {
  id?: number
  has_active_number?: boolean
  /** True si l'user a une subscription Stripe encore valide (active, trialing,
   *  past_due, ou canceled-mais-current_period_end-futur). Utilisé par le
   *  routing post-login pour ne pas renvoyer un user qui a encore accès dans
   *  l'onboarding même s'il a supprimé son numéro Twilio. */
  has_active_subscription?: boolean
  phone_number?: string | null
  preferred_phone_number?: string | null
  /** Mise en service après le paiement : dernier écran fait, et la date où les écrans obligatoires l'ont été. */
  mise_en_service_etape?: string
  mise_en_service_terminee_at?: string | null
  /** Langue que les appelants entendent, choisie à la mise en service ('' avant). */
  langue_principale?: '' | 'fr' | 'en'
  name?: string
  company_size?: string
  /** Secteur choisi à l'inscription (étape 2) ; vide pour les comptes d'avant le champ obligatoire. */
  industry?: string | null
  address1?: string
  city?: string
  province?: string
  postal?: string
  is_toll_free?: boolean
  /** Add-on WhatsApp actif — gate l'inbox WhatsApp (dérivé de CompanyAddon). */
  whatsapp_enabled?: boolean
  admin_disabled?: boolean
  number_active?: boolean
  is_trialing?: boolean
}

/**
 * Payé, mais le numéro choisi n'est pas encore acheté : l'achat se fait sur
 * l'écran Bienvenue, après la vérification du cellulaire. Un client qui quitte
 * avant (le 30 sept 2026, compte test 206) revenait sur le tableau de bord
 * sans numéro, et plus rien ne l'achetait. Le numéro préféré est effacé après
 * la tentative d'achat, réussie ou non : pas de boucle possible.
 */
export function achatNumeroEnAttente(company: CompanyInfo | undefined): boolean {
  return (
    !!company?.has_active_subscription &&
    !company.has_active_number &&
    !company.phone_number &&
    !!company.preferred_phone_number &&
    company.mise_en_service_terminee_at === null
  )
}

/**
 * Paid, but the chosen number could not be bought (taken in the meantime, or
 * refused by the provider): the server cleared the choice. Back to choosing a
 * number, never a dashboard without one.
 */
export function choixNumeroARefaire(company: CompanyInfo | undefined): boolean {
  return (
    !!company?.has_active_subscription &&
    !company.has_active_number &&
    !company.phone_number &&
    !company.preferred_phone_number &&
    company.mise_en_service_terminee_at === null
  )
}

export interface CompaniesResponse {
  companies?: CompanyInfo[]
}

export class ApiHttpError extends Error {
  status: number
  data: unknown
  constructor(status: number, data: unknown) {
    super(`HTTP ${status}`)
    this.name = 'ApiHttpError'
    this.status = status
    this.data = data
  }
}

/** POST /api/token/ — endpoint public, sans interceptor JWT. */
export async function login(input: LoginInput): Promise<LoginResponse> {
  const username = input.email.trim().toLowerCase()
  const res = await fetch(`${api.defaults.baseURL}/api/token/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...deviceHeaders() },
    body: JSON.stringify({ username, password: input.password }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new ApiHttpError(res.status, data)
  }
  return res.json()
}

export interface RegisterInput {
  email: string
  password1: string
  password2: string
  turnstile_token: string
  locale?: string
  /** Optional, unchecked by default: express consent to offers by email (CASL). */
  consentement_offres?: boolean
  /** Where the visitor came from before callizy.ca (lib/provenance). */
  provenance?: Provenance | null
}

export interface RegisterResponse {
  /** dj-rest-auth names the account id `pk`. */
  user?: { pk?: number; id?: number; email?: string }
  detail?: string
}

/** POST /api/auth/registration/ — endpoint public. Le backend envoie un code de vérif email. */
export async function registerAccount(input: RegisterInput): Promise<RegisterResponse> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }
  if (input.locale) headers['Accept-Language'] = input.locale

  const res = await fetch(`${api.defaults.baseURL}/api/auth/registration/`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({
      email: input.email.trim().toLowerCase(),
      password1: input.password1,
      password2: input.password2,
      turnstile_token: input.turnstile_token,
      consentement_offres: !!input.consentement_offres,
      ...(input.provenance ? { provenance: input.provenance } : {}),
    }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new ApiHttpError(res.status, data)
  return data
}

function buildHeaders(locale?: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    // Lets the server recognize a browser remembered after a sign-in code.
    ...deviceHeaders(),
  }
  if (locale) headers['Accept-Language'] = locale
  return headers
}

async function postPublic<T = unknown>(path: string, body: unknown, locale?: string): Promise<T> {
  const res = await fetch(`${api.defaults.baseURL}${path}`, {
    method: 'POST',
    headers: buildHeaders(locale),
    body: JSON.stringify(body),
  })
  const data = (await res.json().catch(() => ({}))) as T
  if (!res.ok) throw new ApiHttpError(res.status, data)
  return data
}

/** POST /api/auth/request-reset-code/ — endpoint public. */
export async function requestResetCode(email: string, locale?: string): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }
  if (locale) headers['Accept-Language'] = locale

  const res = await fetch(`${api.defaults.baseURL}/api/auth/request-reset-code/`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ email: email.trim().toLowerCase() }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new ApiHttpError(res.status, data)
  }
}

export interface VerifyResetCodeInput {
  email: string
  code: string
  newPassword: string
}

/** POST /api/auth/verify-reset-code/ — applique le nouveau mot de passe. */
export async function verifyResetCode(input: VerifyResetCodeInput, locale?: string): Promise<void> {
  await postPublic(
    '/api/auth/verify-reset-code/',
    {
      email: input.email.trim().toLowerCase(),
      code: input.code,
      new_password: input.newPassword,
    },
    locale,
  )
}

export interface VerifyEmailResponse {
  success?: boolean
  access?: string
  refresh?: string
  user_id?: number
  email?: string
  error?: string
}

/** POST /api/auth/verify-email/ — vérifie le code email et retourne les tokens. */
export async function verifyEmail(email: string, code: string): Promise<VerifyEmailResponse> {
  return postPublic<VerifyEmailResponse>('/api/auth/verify-email/', {
    email: email.trim().toLowerCase(),
    code,
  })
}

/** POST /api/auth/send-email-verification/ — envoie un nouveau code. */
export async function sendEmailVerification(email: string, locale?: string): Promise<void> {
  await postPublic(
    '/api/auth/send-email-verification/',
    { email: email.trim().toLowerCase() },
    locale,
  )
}

/** POST /api/auth/magic-link/request/ — déclenche l'envoi d'un email avec un
 *  lien de connexion à usage unique. Réponse toujours 200 côté serveur (anti
 *  user-enumeration), donc on n'a pas besoin de gérer un "email inconnu". */
export async function requestMagicLink(email: string, locale?: string): Promise<void> {
  await postPublic(
    '/api/auth/magic-link/request/',
    { email: email.trim().toLowerCase() },
    locale,
  )
}

/** POST /api/auth/magic-link/verify/ — exchange le token reçu par email contre
 *  des JWT. Même format de réponse que /api/token/. */
export async function verifyMagicLink(token: string): Promise<LoginResponse> {
  return postPublic<LoginResponse>('/api/auth/magic-link/verify/', { token })
}

/** POST /api/auth/lien-appli/ — the iPhone app's "More settings on the web" link:
 *  a one-time code (2 minutes) exchanged for tokens, plus the AI agent to open and,
 *  when set, the section of its page ('agenda': its calendar). */
type AppCodeResponse = LoginResponse & { agent_id?: number | null; page?: string }

export async function exchangeAppCode(code: string): Promise<AppCodeResponse> {
  return postPublic<AppCodeResponse>('/api/auth/lien-appli/', { code })
}

/** POST /api/auth/magic-link/verify-code/ — exchange le code 6 chiffres saisi
 *  par l'utilisateur contre des JWT. Permet de finaliser le login dans la
 *  session d'origine quand l'email arrive sur un autre appareil. */
export async function verifyMagicCode(email: string, code: string): Promise<LoginResponse> {
  return postPublic<LoginResponse>('/api/auth/magic-link/verify-code/', {
    email: email.trim().toLowerCase(),
    code: code.trim(),
  })
}

export interface OAuthTokensResponse {
  success?: boolean
  tokens?: {
    access_token: string
    refresh_token: string
    user_id?: number | string
    email?: string
    first_name?: string
    last_name?: string
    /** Present when the sign-in is risky: the code screen must be passed first. */
    verification?: VerificationState
    /** The account was created by this return from Google. */
    nouveau_compte?: boolean
  }
  error?: string
}

/** GET /api/auth/oauth-tokens/?code=... — échange le code temporaire OAuth contre des tokens JWT. */
export async function exchangeOAuthCode(code: string): Promise<OAuthTokensResponse> {
  const res = await fetch(`${api.defaults.baseURL}/api/auth/oauth-tokens/?code=${encodeURIComponent(code)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', ...deviceHeaders() },
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new ApiHttpError(res.status, data)
  return data as OAuthTokensResponse
}

export async function fetchProfile(): Promise<UserProfile> {
  const { data } = await api.get<UserProfile>('/api/user/profile/')
  return data
}

export async function fetchCompanies(): Promise<CompaniesResponse> {
  const { data } = await api.get<CompaniesResponse>('/api/user/companies/')
  return data
}

export interface CompanyDetail {
  id: number
  name?: string
  phone_number?: string | null
  preferred_phone_number?: string | null
  company_size?: string
  address1?: string
  city?: string
  province?: string
  postal?: string
}

export async function fetchCompanyDetail(companyId: number): Promise<CompanyDetail> {
  const { data } = await api.get<CompanyDetail>(`/api/company/${companyId}/`)
  return data
}

/** No phone_number: the mobile changes only through sendPhoneCode then verifyPhoneCode. */
export interface UpdateProfileInput {
  first_name?: string
  last_name?: string
  job_title?: string
  language?: string
  forward_number?: string
  dnd_enabled?: boolean
  sms_push_notifications_enabled?: boolean
  sms_email_notifications_enabled?: boolean
  preferred_caller_id?: string
}

export async function updateUserProfile(input: UpdateProfileInput): Promise<UserProfile> {
  const { data } = await api.patch<UserProfile>('/api/user/profile/', input)
  return data
}

export async function inviteAgent(email: string): Promise<void> {
  await api.post('/api/agents/invite/', { email })
}

/** POST /api/auth/phone/send-code/ : un code à 6 chiffres part par texto vers ce cellulaire. */
export async function sendPhoneCode(phone: string, lang: string): Promise<{ ok: boolean; phone: string; country: string }> {
  const { data } = await api.post<{ ok: boolean; phone: string; country: string }>('/api/auth/phone/send-code/', { phone, lang })
  return data
}

/** POST /api/auth/phone/verify-code/ : le cellulaire devient vérifié. */
export async function verifyPhoneCode(code: string): Promise<{ ok: boolean; phone: string; phone_verified_at: string }> {
  const { data } = await api.post<{ ok: boolean; phone: string; phone_verified_at: string }>('/api/auth/phone/verify-code/', { code })
  return data
}
